import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import { MenuItem } from '@mui/material';
import { useForm } from 'react-hook-form';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCodeSystem, useValueSet } from 'src/@nicheaim/fhir-react';
import { WrappedPatient } from 'src/@nicheaim/fhir-base/wrappers/Patient';
import { ValueSetWrapper } from 'src/@nicheaim/fhir-base/wrappers/ValueSet';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { convertValueToValueSet, getCodeSystemFromConcept } from './common-utils';
import { Box, Button, Dialog, DialogActions, DialogTitle, Grid, Stack } from '@mui/material';

type FormValues = {
  topic: string;
  description: string;
};

type Props = {
  open: boolean;
  onCancel: VoidFunction;
  handleCreate: (data: any) => void;
  patient: WrappedPatient | null;
  resource: any;
  typeNote: string;
};

export default function NoteAddForm({
  open,
  patient,
  resource,
  typeNote,
  onCancel,
  handleCreate,
}: Props) {
  const authUser = useAuth().getCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const typeResource = resource?.map((x1) => ({reference: `${x1?.resourceType}/${x1?.id}`}));

  const [topicList] = useValueSet('communication-topic', { map: ValueSetWrapper });
  const [communicationCategory] = useValueSet('crs-communication-categories', { map: ValueSetWrapper });
  const categoryCommunicationCoding = convertValueToValueSet(typeNote, communicationCategory);

  const EventSchema = Yup.object().shape({
    topic: Yup.string().required('Topic is required'),
    description: Yup.string().required('Note is required'),
  });

  const defaultValues = {
    topic: '',
    description: '',
  };

  const methods = useForm<FormValues>({ resolver: yupResolver(EventSchema), defaultValues });

  const { 
    control, 
    reset, 
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const mapFormDataToCommunication = (data: FormValues): any => {
    const mappedData: any = { resourceType: 'Communication' };

    mappedData.status = 'completed';
    mappedData.sent = new Date();
    mappedData.category = [
      {
        coding: [categoryCommunicationCoding],
        text: categoryCommunicationCoding?.display,
      }
    ];
    mappedData.subject = {
      reference: `Patient/${patient?.id}`,
      display: patient?.getFullName(),
    };
    mappedData.sender = {
      ...(!isEmpty(authUser.user_fhir_uri.trim()) && {
        reference: `Practitioner/${getFhirIdFromEntity(authUser.user_fhir_uri)}`,
      }),
      display: authUser.name,
    };

    if(typeResource?.length > 0){
      mappedData.partOf = typeResource;
    }

    if (data?.description) {
      mappedData.payload = [
        {
          contentString: data.description,
        },
        {
          contentReference: {
            display: data.description,
          },
        },
      ];
    }

    if (data?.topic) {
      mappedData.topic = {
        coding: [
          {
            ...convertValueToValueSet(data?.topic, topicList),
          },
        ],
        text: convertValueToValueSet(data?.topic, topicList)?.display,
      };
    }

    return mappedData;
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const noteData = mapFormDataToCommunication(data);
      await handleCreate(noteData);
      enqueueSnackbar('Communication created successfully!');
      handleClose();
    } catch (error) {
      enqueueSnackbar('Communication was not created', {
        variant: 'error',
        autoHideDuration: 6000,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="sm">
      <DialogTitle>Notes</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <Stack spacing={2} sx={{ p: 2 }}>
              <RHFSelect name="topic" label="Topic">
                <MenuItem disabled/>
                {topicList?.asList().map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option?.display}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Stack>

            <Stack spacing={2} sx={{ p: 2 }}>
              <RHFTextField name="description" label="Note" multiline rows={4} />
            </Stack>
          </Grid>
        </Grid>

        <Stack spacing={2} alignItems="center">
          <DialogActions>
            <Box sx={{ flexGrow: 1 }} />

            <Button variant="outlined" color="info" onClick={handleClose}>
              Cancel
            </Button>

            <Button type="submit" color="info" variant="contained" disabled={isSubmitting}>
              Submit
            </Button>
          </DialogActions>
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
